// Client auth for an Estra app: sign-in methods, a persisted session, ID tokens refreshed before
// they expire, and onAuthStateChanged. Pair it with the Firestore API:
//   const auth = createAuth({ url });
//   const db = getFirestore(httpTransport({ url, getToken: () => auth.getIdToken() }));
import { EstraClientError } from "./transport.ts";

export interface AuthUser {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  displayName: string | null;
  photoUrl: string | null;
  anonymous: boolean;
  disabled: boolean;
  providers: string[];
  createdAt: number;
  lastSignInAt: number | null;
  /** Sessions from before this time (ms) were revoked; null when never revoked. */
  tokensValidAfter?: number | null;
  claims: Record<string, unknown>;
}

interface Stored {
  idToken: string;
  refreshToken: string;
  /** ms since epoch. */
  expiresAt: number;
  user: AuthUser;
}

/** Where the session lives: localStorage on the web, Keychain/Keystore on mobile, memory in tests. */
export interface SessionStorage {
  get(key: string): string | null | Promise<string | null>;
  set(key: string, value: string): void | Promise<void>;
  remove(key: string): void | Promise<void>;
}

export const memoryStorage = (): SessionStorage => {
  const m = new Map<string, string>();
  return { get: (k) => m.get(k) ?? null, set: (k, v) => void m.set(k, v), remove: (k) => void m.delete(k) };
};
type WebStorage = { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void };
const webStorage = () => (globalThis as { localStorage?: WebStorage }).localStorage;
export const browserStorage = (): SessionStorage => ({
  get: (k) => webStorage()?.getItem(k) ?? null,
  set: (k, v) => webStorage()?.setItem(k, v),
  remove: (k) => webStorage()?.removeItem(k),
});

/** The slice of `window` the popup/redirect flows use (injectable for tests). */
export interface WindowLike {
  location: { origin: string; href: string; hash: string; assign(url: string): void };
  open(url: string, target?: string, features?: string): { closed: boolean; location: { href: string }; close(): void } | null;
  addEventListener(type: "message", fn: (e: { origin: string; data: unknown; source?: unknown }) => void): void;
  removeEventListener(type: "message", fn: (e: { origin: string; data: unknown; source?: unknown }) => void): void;
  history?: { replaceState(data: unknown, unused: string, url: string): void };
}

export type OAuthProvider = "google.com" | "apple.com" | "github.com";

export interface AuthOptions {
  url: string;
  /** Browser window for popup/redirect sign-in (defaults to globalThis.window). */
  window?: WindowLike;
  storage?: SessionStorage;
  fetch?: typeof fetch;
  /** Refresh this long before expiry (default 5 min). */
  refreshMarginMs?: number;
}

export type Unsubscribe = () => void;

export function createAuth(o: AuthOptions) {
  const base = o.url.replace(/\/$/, "");
  const storage = o.storage ?? (webStorage() ? browserStorage() : memoryStorage());
  const f = o.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const margin = o.refreshMarginMs ?? 5 * 60_000;
  const key = `estra.session:${base}`;
  const listeners = new Set<(u: AuthUser | null) => void>();
  const win = (): WindowLike => o.window ?? ((globalThis as { window?: WindowLike }).window as WindowLike) ?? fail("no window: popup/redirect sign-in needs a browser");
  const slug = (p: OAuthProvider) => p.replace(/\.com$/, "");
  const startUrl = (p: OAuthProvider, q: Record<string, string>) => `${base}/v1/auth/oauth/${slug(p)}/start?${new URLSearchParams(q)}`;
  const estraOrigin = new URL(base).origin;

  /** Opens the provider popup (synchronously, so browsers allow it) and resolves with the one-time code. */
  function popupCode(p: OAuthProvider, linkToken?: Promise<string>): Promise<string> {
    const w = win();
    const popup = w.open(linkToken ? "about:blank" : startUrl(p, { mode: "popup", origin: w.location.origin }), "estra-auth", "popup,width=500,height=640");
    if (!popup) return Promise.reject(new EstraClientError("popup-blocked", "the browser blocked the sign-in popup"));
    return new Promise<string>((resolve, reject) => {
      let done = false;
      const finish = (err: EstraClientError | null, code?: string) => {
        if (done) return;
        done = true;
        clearInterval(timer);
        w.removeEventListener("message", onMessage);
        if (err) reject(err);
        else resolve(code!);
      };
      const onMessage = (e: { origin: string; data: unknown }) => {
        if (e.origin !== estraOrigin) return;
        const d = e.data as { type?: string; code?: string; error?: { code: string; message: string } };
        if (d?.type !== "estra-oauth") return;
        if (d.code) finish(null, d.code);
        else finish(new EstraClientError(d.error?.code ?? "internal", d.error?.message ?? "sign-in failed"));
      };
      w.addEventListener("message", onMessage);
      const timer = setInterval(() => {
        if (popup.closed) finish(new EstraClientError("popup-closed-by-user", "the sign-in popup was closed"));
      }, 400);
      if (linkToken)
        linkToken.then(
          (t) => (popup.location.href = startUrl(p, { mode: "popup", origin: w.location.origin, linkToken: t })),
          (e) => {
            popup.close();
            finish(e instanceof EstraClientError ? e : new EstraClientError("internal", String(e)));
          },
        );
    });
  }
  let session: Stored | null = null;
  let loaded: Promise<void> | null = null;
  let refreshing: Promise<Stored | null> | null = null;

  const emit = () => listeners.forEach((l) => l(session?.user ?? null));

  async function load() {
    loaded ??= (async () => {
      const raw = await storage.get(key);
      try {
        session = raw ? (JSON.parse(raw) as Stored) : null;
      } catch {
        session = null;
      }
    })();
    return loaded;
  }

  async function save(s: Stored | null) {
    session = s;
    if (s) await storage.set(key, JSON.stringify(s));
    else await storage.remove(key);
    emit();
  }

  async function post<T>(route: string, body: unknown, bearer?: string): Promise<T> {
    const headers: Record<string, string> = { "content-type": "application/json" };
    if (bearer) headers.authorization = `Bearer ${bearer}`;
    const r = await f(`${base}/v1/auth/${route}`, { method: "POST", headers, body: JSON.stringify(body) });
    const j = (await r.json().catch(() => ({}))) as { error?: { code: string; message: string } } & T;
    if (!r.ok) throw new EstraClientError(j.error?.code ?? "unavailable", j.error?.message ?? `HTTP ${r.status}`);
    return j;
  }

  type SessionOut = { uid: string; idToken: string; refreshToken: string; expiresIn: number; user: AuthUser };
  const start = async (p: Promise<SessionOut>) => {
    const s = await p;
    await save({ idToken: s.idToken, refreshToken: s.refreshToken, expiresAt: Date.now() + s.expiresIn * 1000, user: s.user });
    return s.user;
  };

  async function refresh(): Promise<Stored | null> {
    refreshing ??= (async () => {
      const cur = session;
      if (!cur) return null;
      try {
        const s = await post<SessionOut>("refresh", { refreshToken: cur.refreshToken });
        await save({ idToken: s.idToken, refreshToken: s.refreshToken, expiresAt: Date.now() + s.expiresIn * 1000, user: s.user });
        return session;
      } catch (e) {
        // Only a refusal signs out; a network failure keeps the session for the next try.
        if (e instanceof EstraClientError && e.code === "invalid-credential") await save(null);
        throw e;
      }
    })().finally(() => {
      refreshing = null;
    });
    return refreshing;
  }

  async function linkToken(): Promise<string> {
    const token = await currentToken();
    return (await post<{ linkToken: string }>("oauth/link-token", {}, token)).linkToken;
  }

  return {
    get currentUser(): AuthUser | null {
      return session?.user ?? null;
    },
    /** Resolves once the stored session (if any) is loaded. */
    ready: () => load(),
    onAuthStateChanged(cb: (u: AuthUser | null) => void): Unsubscribe {
      listeners.add(cb);
      void load().then(() => cb(session?.user ?? null));
      return () => listeners.delete(cb);
    },
    /** A valid access token (refreshed when near expiry), or null when signed out. */
    async getIdToken(forceRefresh = false): Promise<string | null> {
      await load();
      if (!session) return null;
      if (forceRefresh || session.expiresAt - Date.now() < margin) return (await refresh())?.idToken ?? null;
      return session.idToken;
    },
    signUp: async (email: string, password: string, displayName?: string) => {
      await load();
      return start(post("signup", { email, password, ...(displayName ? { displayName } : {}) }, session?.user.anonymous ? session.idToken : undefined));
    },
    signIn: (email: string, password: string) => start(post("signin", { email, password })),
    /** Google/Apple: pass the ID token from Google Identity Services or Sign in with Apple. */
    signInWithIdToken: (provider: "google.com" | "apple.com", idToken: string, o: { nonce?: string; displayName?: string } = {}) => start(post("signin/idp", { provider, idToken, ...o })),
    signInAnonymously: () => start(post("signin/anonymous", {})),
    /** Google / Apple / GitHub in a popup (call from a click handler). */
    signInWithPopup: async (provider: OAuthProvider) => start(post("oauth/exchange", { code: await popupCode(provider) })),
    // Popups must open synchronously inside the click handler (browsers block them after an await),
    // so every popup flow opens first and does its async work afterwards.
    /** Link a provider to the signed-in account (an anonymous account becomes a full one, same uid). */
    linkWithPopup: async (provider: OAuthProvider) => start(post("oauth/exchange", { code: await popupCode(provider, linkToken()) })),
    /** Sign in again with the same account (before sensitive changes). Fails if a different account answers. */
    reauthenticateWithPopup: async (provider: OAuthProvider) => {
      const codeP = popupCode(provider);
      await load();
      const uid = session?.user.uid;
      const code = await codeP;
      if (!uid) throw new EstraClientError("unauthenticated", "sign in first");
      const s = await post<SessionOut>("oauth/exchange", { code });
      if (s.uid !== uid) throw new EstraClientError("user-mismatch", "a different account signed in");
      return start(Promise.resolve(s));
    },
    /** Full-page redirect sign-in; finish with getRedirectResult() on the page you return to. */
    signInWithRedirect: (provider: OAuthProvider, returnTo?: string) => {
      const w = win();
      w.location.assign(startUrl(provider, { mode: "redirect", returnTo: returnTo ?? w.location.href }));
    },
    linkWithRedirect: async (provider: OAuthProvider, returnTo?: string) => {
      const w = win();
      w.location.assign(startUrl(provider, { mode: "redirect", returnTo: returnTo ?? w.location.href, linkToken: await linkToken() }));
    },
    /** After a redirect: the signed-in user, null when this page load isn't a sign-in return. */
    getRedirectResult: async (): Promise<AuthUser | null> => {
      const w = win();
      const h = new URLSearchParams(w.location.hash.replace(/^#/, ""));
      const code = h.get("estra_auth");
      const error = h.get("estra_auth_error");
      if (!code && !error) return null;
      w.history?.replaceState(null, "", w.location.href.split("#")[0]!);
      if (error) throw new EstraClientError(error, "sign-in failed");
      return start(post("oauth/exchange", { code }));
    },
    /** Native apps: link a Google/Apple ID token to the signed-in account. */
    linkWithIdToken: async (provider: "google.com" | "apple.com", idToken: string, nonce?: string) => start(post("link/idp", { provider, idToken, ...(nonce ? { nonce } : {}) }, await currentToken())),
    sendSignInLink: (email: string) => post("email/send", { mode: "signIn", email }),
    /** Finish a link from the email: mode + oobCode come from its query string. */
    signInWithLink: (oobCode: string) => start(post("email/confirm", { mode: "signIn", oobCode })),
    sendPasswordReset: (email: string) => post("email/send", { mode: "resetPassword", email }),
    confirmPasswordReset: (oobCode: string, newPassword: string) => post("email/confirm", { mode: "resetPassword", oobCode, newPassword }),
    sendEmailVerification: async () => post("email/send", { mode: "verifyEmail" }, (await load(), session?.idToken)),
    applyEmailVerification: async (oobCode: string) => {
      await post("email/confirm", { mode: "verifyEmail", oobCode });
      if (session) await refresh().catch(() => null);
    },
    updateProfile: async (p: { displayName?: string | null; photoUrl?: string | null; password?: string; currentPassword?: string }) => {
      const token = await currentToken();
      const { user } = await post<{ user: AuthUser }>("me/update", p, token);
      if (session) await save({ ...session, user });
      return user;
    },
    deleteUser: async () => {
      await post("me/delete", {}, await currentToken());
      await save(null);
    },
    signOut: async () => {
      await load();
      const s = session;
      await save(null);
      if (s) await post("signout", { refreshToken: s.refreshToken }).catch(() => null);
    },
  };

  async function currentToken(): Promise<string> {
    await load();
    if (!session) throw new EstraClientError("unauthenticated", "sign in first");
    if (session.expiresAt - Date.now() < margin) await refresh();
    return session!.idToken;
  }
}

export type EstraAuth = ReturnType<typeof createAuth>;

function fail(message: string, code = "internal"): never {
  throw new EstraClientError(code, message);
}
